import React, { useState, useEffect, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import debounce from 'lodash.debounce'
import cn from 'comma-number'
import { getRoundCommaNum } from 'Utils/inputUtils'

import CalcOutputBox from './components/CalcOutputBox'
import CalcOptionDropdown from './components/CalcOptionDropdown'
import PreApproved from './components/PreApproved'
import {
  _InputWithLabel,
  _ItemBoxCol,
  _Calculator,
  _ErrorMsg,
  _PurchasePriceDownPayment,
  _InterestAmortizationFrequency,
  _InterestAmortization,
  _Title,
  _AdditionalExpenses,
  _ResultRow,
  _CalculatorContainer,
  _SelectWithLabel,
} from './styles'
import {
  interestRateTips,
  totalMortgageTips,
  priceTips,
  downPayPercents,
  frequencies,
  // onDownPaymentBlur,
  strToNum,
  mortgagePaymentWithPremium,
  getInitState,
  yearsValue,
} from './constant'

const MortgageCalculator = ({ googleAnalyticsEventsHandler, mls, ...props }) => {
  const [state, setState] = useState(getInitState(props))
  const [frequencyDisplayValue, setFrequencyDisplayValue] = useState('Monthly')
  const calculatorRef = useRef(null)
  const prevProps = useRef(props)

  useEffect(() => {
    if(
      prevProps.current.price !== props.price ||
      prevProps.current.rate !== props.rate
    ) {
      setState(getInitState(props))
    }
    prevProps.current = props
  }, [props])

  const debounceGoogle = useCallback(
    debounce((name, number) => {
      googleAnalyticsEventsHandler(`[${name}],[${number}]`)
    }, 700),
    [],
  )

  const handleMaintenanceChange = ({ value }) => {
    const mainFee = strToNum(value)
    setState(prevState => ({ ...prevState, maintenance: mainFee }))
    debounceGoogle('Main Fee Change', `$${value}`)
  }

  const handleTaxesChange = ({ value }) => {
    const propertyTax = strToNum(value)
    setState(prevState => ({ ...prevState, taxes: propertyTax }))
    debounceGoogle('Taxes Change', `$${value}`)
  }

  const onPriceChange = data => {
    const val = strToNum(data?.value)
    const { downPaymentPercent } = state

    setState(prevState => ({
      ...prevState,
      price: cn(val),
      unitPrice: val,
      downPayment: cn((val * (downPaymentPercent * 0.01)).toFixed(0)),
    }))
    debounceGoogle('Price Change', `$${val}`)
  }

  const onTermChange = data => {
    if(!data.value || data.value.match(/^([0-9]|[1-9][0-9])$/g)) {
      const val = data.value && strToNum(data.value)
      setState(prevState => ({ ...prevState, mortgageAmortization: cn(val) }))
      debounceGoogle('Amortization Change', `${val} years`)
    }
  }

  const onDownPaymentChange = data => {
    // minimum down is always 5%
    // enforce minimum down between 5% and 7.5% for homes between 500K and 1M
    // enforce minimum down pay if its over 1M
    let value = data?.value
    if(typeof value === 'string') {
      value = value.replace(/,/g, '')
    }

    if(value < state.unitPrice && (!value || value.match(/^[0-9]{1,10}$/g))) {
      const downPaymentPercent = (value / state.unitPrice) * 100
      setState(prevState => ({
        ...prevState,
        downPayment: cn(value),
        downPaymentPercent: downPaymentPercent.toFixed(0),
      }))
      debounceGoogle('Down Payment Change', `$${value}`)
    }
  }

  const onRateChange = ({ value }) => {
    const mortgageRate = value > 99.99 ? 99.99 : value < 0 ? 0 : value || ''
    setState(prevState => ({ ...prevState, mortgageRate }))
    debounceGoogle('Rate Change', `${value}%`)
  }

  const onDropdownDownPayItemClick = value => {
    setState(prevState => ({
      ...prevState,
      downPayment: cn((state.unitPrice * (value * 0.01)).toFixed(0)),
      downPaymentPercent: value,
    }))
  }

  const onDropdownTermItemClick = amortization => {
    setState(prevState => ({
      ...prevState,
      mortgageAmortization: amortization,
    }))
  }

  const onDropdownFrequencyItemClick = frequency => {
    const selectedFrequency = frequencies[frequency.value]
    setState(prevState => ({ ...prevState, frequency: selectedFrequency }))
    setFrequencyDisplayValue(selectedFrequency.short.toLowerCase())
    googleAnalyticsEventsHandler(
      `[Frequency Change][${selectedFrequency.short}]`,
    )
  }

  // http://www.themortgagestoreonline.com/canadianhomemortgagepaymentformula.html
  // http://investexcel.net/how-to-calculate-mortgage-payments-in-excel/
  const {
    taxes,
    maintenance,
    mortgageAmortization,
    unitPrice,
    downPayment,
    mortgageRate,
    downPaymentPercent,
    frequency,
  } = state

  let downPaymentVal = downPayment
  const frequencyVal = frequency?.value
  if(typeof downPayment === 'string') {
    downPaymentVal = Number(downPaymentVal.replace(/,/g, ''))
  }
  const mortgagePrincipal = unitPrice - downPaymentVal
  const rate = mortgageRate / 100

  // step 6
  const halfRate = rate * 0.5
  const halfRateOne = halfRate + 1

  // n = totalInterestPeriods
  const n = (mortgageAmortization || 1) * frequencyVal

  const compoundOverFrequency = 2 / frequencyVal
  const exponent = Math.pow(halfRateOne, compoundOverFrequency); // eslint-disable-line
  // R = periodic interest rate
  const r = exponent - 1

  const rPlusOnePowN = Math.pow(r + 1, n); // eslint-disable-line

  const originalMortgagePayment = Number(
    (mortgagePrincipal * r * (rPlusOnePowN / (rPlusOnePowN - 1))).toFixed(),
  )
  const mortgagePayment = mortgagePaymentWithPremium(
    originalMortgagePayment,
    downPaymentPercent,
  )
  const insurancePremium = (
    mortgagePayment - originalMortgagePayment
  ).toFixed()
  let eachPayment = mortgagePayment + (taxes + maintenance * 12) / frequencyVal; // eslint-disable-line
  // format the numbers
  // const downPaymentAmount = getRoundCommaNum(downPaymentVal)
  const mortgageAmount = getRoundCommaNum(mortgagePrincipal)
  // const maintenanceAmount = getRoundCommaNum(maintenance)
  // const taxesAmount = getRoundCommaNum(taxes)
  eachPayment = getRoundCommaNum(eachPayment)
  const additionExpenses = getRoundCommaNum(
    (taxes + maintenance * 12) / frequencyVal,
  )

  return (
    <_CalculatorContainer>
      <_Calculator data-test-id="mortgageCalculator" ref={calculatorRef}>
        <_PurchasePriceDownPayment>
          <_ItemBoxCol xs={6} md={3}>
            <_InputWithLabel
              withDollar
              id="price"
              name="price"
              label="Property Price"
              type="text"
              value={state.price}
              onChange={onPriceChange}
              enableTooltip
              toolTipInfo={{
                body: priceTips,
                collapsibleIdSuffix: props.collapsibleIdSuffix,
              }}
            />
          </_ItemBoxCol>
          <_ItemBoxCol xs={6} md={3}>
            <_InputWithLabel
              withDollar
              id="downPayment"
              name="downPayment"
              label="Down Payment"
              type="text"
              value={downPayment}
              onChange={onDownPaymentChange}
            >
              <CalcOptionDropdown
                label={downPaymentPercent + '% '}
                currentVal={downPaymentPercent}
                showBorder
              >
                {downPayPercents.map(percent => (
                  <li // eslint-disable-line
                    key={'percent' + percent}
                    onClick={() => {
                      onDropdownDownPayItemClick(percent)
                      googleAnalyticsEventsHandler(
                        `[Down Payment Change][${percent}%]`,
                      )
                    }}
                  >
                    {percent}%
                  </li>
                ))}
              </CalcOptionDropdown>
              {state.errorMsg && <_ErrorMsg>{state.errorMsg}</_ErrorMsg>}
            </_InputWithLabel>
          </_ItemBoxCol>
        </_PurchasePriceDownPayment>
        <_InterestAmortizationFrequency>
          <_InterestAmortization>
            <_ItemBoxCol xs={6} md={3}>
              <_InputWithLabel
                withPercent
                id="rate"
                name="rate"
                label="Interest Rate"
                type="number"
                min={0}
                step={0.01}
                value={state.mortgageRate}
                onChange={onRateChange}
                enableTooltip
                smallInputField
                toolTipInfo={{
                  body: interestRateTips,
                  collapsibleIdSuffix: props.collapsibleIdSuffix,
                }}
              />
            </_ItemBoxCol>
            <_ItemBoxCol xs={6} md={3}>
              <_InputWithLabel
                id="amortization"
                name="amortization"
                label={'Amortization'}
                onChange={onTermChange}
                value={`${mortgageAmortization}`}
                smallInputField
              >
                <CalcOptionDropdown
                  label={'years'}
                  currentVal={mortgageAmortization}
                >
                  {yearsValue.map(value => (
                    <li // eslint-disable-line
                      key={'amortization' + value}
                      onClick={() => {
                        onDropdownTermItemClick(value)
                        googleAnalyticsEventsHandler(
                          `[Amortization Change][${value} years]`,
                        )
                      }}
                    >
                      {value} years
                    </li>
                  ))}
                </CalcOptionDropdown>
              </_InputWithLabel>
            </_ItemBoxCol>
          </_InterestAmortization>
          <_ItemBoxCol xs={6} md={3} mdOffset={6} xsOffset={0} style={{ width: '100%' }}>
            <_SelectWithLabel
              id="frequency"
              name="frequency"
              label={'Payment Frequency'}
              onChange={onDropdownFrequencyItemClick}
              selectedVal={`${state?.frequency?.short}`}
              withSvg
            >
              {Object.keys(frequencies).map((freq, index) => (
                <option // eslint-disable-line
                  key={freq}
                  tabIndex={index}
                >
                  {freq}
                </option>
              ))}
            </_SelectWithLabel>
          </_ItemBoxCol>
        </_InterestAmortizationFrequency>
        <_ResultRow>
          <CalcOutputBox
            enableTooltip
            tooltipInfo={{ body: totalMortgageTips }}
            label={'Total Mortgage'}
            output={String(mortgageAmount)}
            bold
          />
          <CalcOutputBox
            label={'Mortgage Payment'}
            output={
              !isNaN(mortgagePayment)
                ? String(cn(mortgagePayment.toFixed()))
                : '-'
            }
            bold
          />
        </_ResultRow>

        <_ResultRow>
          <CalcOutputBox
            label={'Principal & Interest'}
            output={String(cn(originalMortgagePayment.toFixed()))}
          />
          <CalcOutputBox
            label={'Insurance Premium'}
            output={String(insurancePremium)}
          />
        </_ResultRow>
        <_ItemBoxCol xs={6} md={3}>
          {
        !props.doNotShowPreApproved ?
          <PreApproved
            unitPrice={unitPrice}
            downPayment={downPayment}
            address={props.address}
            mobileContainer
            mls={mls}
          /> : ''
      }
        </_ItemBoxCol>
        <_Title>Additional Expenses</_Title>
        <_AdditionalExpenses>
          <_ItemBoxCol xs={6} md={3}>
            <_InputWithLabel
              withDollar
              id="maintenance"
              name="maintenance"
              label="Maintenance Fees"
              type="text"
              value={getRoundCommaNum(state.maintenance)}
              onChange={handleMaintenanceChange}
              withText={'monthly'}
            />
          </_ItemBoxCol>
          <_ItemBoxCol xs={6} md={3}>
            <_InputWithLabel
              withDollar
              id="taxes"
              name="taxes"
              label="Property Taxes"
              type="text"
              value={cn(getRoundCommaNum(state.taxes))}
              onChange={handleTaxesChange}
              withText={'yearly'}
            />
          </_ItemBoxCol>
        </_AdditionalExpenses>
        <_ResultRow>
          <CalcOutputBox
            label={'Additional Fees'}
            output={String(additionExpenses)}
            suffix={frequencyDisplayValue}
          />
          <CalcOutputBox
            label={'Total Payment'}
            output={String(eachPayment)}
            bold
            suffix={frequencyDisplayValue}
          />
        </_ResultRow>
      </_Calculator>
      {
        !props.doNotShowPreApproved ?
          <PreApproved
            unitPrice={unitPrice}
            downPayment={downPayment}
            address={props.address}
            mls={mls}
          /> : ''
      }

    </_CalculatorContainer>
  )
}

MortgageCalculator.propTypes = {
  price: PropTypes.number,
  address: PropTypes.string,
  googleAnalyticsEvent: PropTypes.func,
  // submitMortgage: PropTypes.func,
  rate: PropTypes.number,
  user: PropTypes.shape({}),
  collapsibleIdSuffix: PropTypes.string,
  doNotShowPreApproved: PropTypes.bool,
  googleAnalyticsEventsHandler: PropTypes.func,
  mls: PropTypes.string,
}

export default MortgageCalculator
